import { ChangeDetectionStrategy, Component, ElementRef, Injector, afterNextRender, effect, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';

/**
 * The inline editing panel for a text field opened from the "Toevoegen" menu (Figma: "Add note to
 * driver" with Cancel / Save). The field itself is projected; this only frames it.
 */
@Component({
  selector: 'dd-reveal-panel',
  // Esc from the focused field inside bubbles here.
  host: { '(keydown.escape)': 'onEscape()' },
  template: `
    <!-- One projection whether open or not, so the field inside is never destroyed by opening it. -->
    <div [class.dd-reveal-panel]="open()" [attr.data-cy]="open() ? 'reveal-panel' : null">
      <!-- No title when the projected field brings its own label (a Formly field does). -->
      @if (open() && title()) {
        <p class="dd-reveal-panel__title">{{ title() }}</p>
      }
      <div class="flow-root"><ng-content /></div>
      @if (open() && error()) {
        <p class="dd-reveal-panel__error" role="alert" data-cy="reveal-error">{{ error() }}</p>
      }
      @if (open()) {
        <div class="dd-reveal-panel__actions">
          <button mat-stroked-button type="button" class="dd-reveal-panel__button" (click)="cancelEdit.emit()" data-cy="reveal-cancel">
            Annuleren
          </button>
          <button mat-flat-button type="button" class="dd-action-button dd-reveal-panel__button" (click)="saveEdit.emit()" data-cy="reveal-save">
            Opslaan
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }
    /* Figma: Surface/Raised, padding 12, radius 8; buttons 36px right-aligned. */
    .dd-reveal-panel {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px;
      border-radius: 8px;
      background: var(--dd-surface-subdued);
    }
    .dd-reveal-panel__title {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
    }
    .dd-reveal-panel__error {
      font-size: 12px;
      line-height: 1.4;
      color: var(--dd-field-fg-error); /* same as a field error */
    }
    .dd-reveal-panel__actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
    /* !important: the global button rules pin 40px with it (_buttons.scss). */
    .dd-reveal-panel__button {
      height: 36px !important;
      min-height: 36px !important;
      padding: 0 16px !important;
      border-radius: 8px !important;
    }
  `,
  imports: [MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevealPanelComponent {
  readonly title = input('');
  /** Closed, only the projected field shows — the owner decides whether that is at all. */
  readonly open = input(true);
  /** Shown above the buttons, e.g. after Opslaan on an empty field. */
  readonly error = input<string | null>(null);
  readonly cancelEdit = output<void>();
  readonly saveEdit = output<void>();

  protected onEscape(): void {
    if (this.open()) this.cancelEdit.emit();
  }

  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #injector = inject(Injector);

  constructor() {
    // Opening moves focus into the field, so typing can start at once (and Esc reaches the panel).
    effect(() => {
      if (!this.open()) return;
      // The text first: in a note + photo-flag group the flag must not take the focus.
      const host = this.#host.nativeElement;
      afterNextRender(() => (host.querySelector<HTMLElement>('textarea, input:not([type=checkbox])') ?? host.querySelector<HTMLElement>('input, select'))?.focus(), {
        injector: this.#injector,
      });
    });
  }
}
